<template>
  <footer
    class="z-10 border-t border-neutral-200 bg-neutral-50 text-neutral-600 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-400"
    :class="isSimple ? '' : 'py-12'"
    :dir="dir"
  >
    <div class="mx-auto w-full max-w-(--ui-container) px-4 sm:px-6 lg:px-8" :class="isSimple ? 'py-8 lg:py-10' : ''">
      <div
        v-if="!isSimple && (showBrand || (showColumns && resolvedColumns.length))"
        class="grid gap-10 md:grid-cols-12"
      >
        <div
          v-if="showBrand"
          class="md:col-span-4"
        >
          <button
            v-if="builderPreview"
            type="button"
            class="flex items-center gap-3 text-start"
            @click="handleNavClick('/')"
          >
            <UAvatar
              v-if="logoImage"
              :src="logoImage"
              alt="Logo"
              size="md"
              class="rounded-lg"
            />
            <span class="text-xl font-bold text-gray-900 dark:text-white">{{ resolvedLogoText }}</span>
          </button>
          <NuxtLink
            v-else
            to="/"
            class="flex items-center gap-3"
          >
            <UAvatar
              v-if="logoImage"
              :src="logoImage"
              alt="Logo"
              size="md"
              class="rounded-lg"
            />
            <span class="text-xl font-bold text-gray-900 dark:text-white">{{ resolvedLogoText }}</span>
          </NuxtLink>

          <p
            v-if="resolvedTagline"
            class="mt-4 max-w-sm text-sm leading-relaxed text-gray-600 dark:text-gray-400"
          >
            {{ resolvedTagline }}
          </p>

          <div
            v-if="showSocialLinks && resolvedSocialLinks.length"
            class="mt-5 flex flex-wrap gap-1.5"
          >
            <a
              v-for="(link, i) in resolvedSocialLinks"
              :key="i"
              :href="link.url"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-500 transition hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white"
              :aria-label="link.url"
            >
              <UIcon
                :name="link.icon || 'i-heroicons-globe-alt'"
                class="size-5"
              />
            </a>
          </div>
        </div>

        <div
          v-for="(column, ci) in visibleColumns"
          :key="ci"
          class="md:col-span-2"
        >
          <h3 class="text-sm font-semibold uppercase tracking-wide text-gray-900 dark:text-white">
            {{ column.title }}
          </h3>
          <ul class="mt-4 space-y-2.5">
            <li
              v-for="(link, li) in column.links"
              :key="li"
            >
              <button
                v-if="builderPreview"
                type="button"
                class="text-sm text-gray-600 transition hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
                @click="handleNavClick(link.to)"
              >
                {{ link.label }}
              </button>
              <NuxtLink
                v-else
                :to="link.to"
                class="text-sm text-gray-600 transition hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
              >
                {{ link.label }}
              </NuxtLink>
            </li>
          </ul>
        </div>
      </div>

      <div
        v-if="hasContact"
        class="grid gap-8 sm:grid-cols-2 lg:grid-cols-3"
        :class="isSimple ? '' : 'mt-10'"
      >
        <section
          v-if="showPhone && phoneItems.length"
          class="flex min-w-0 gap-3"
          :class="isContactOnly ? 'rounded-2xl border border-neutral-200/80 bg-white/70 p-4 dark:border-neutral-800 dark:bg-neutral-900/50' : ''"
        >
          <span class="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary" aria-hidden="true">
            <UIcon name="i-lucide-phone" class="size-4" />
          </span>
          <div class="min-w-0">
            <h3 class="text-xs font-semibold tracking-wide text-neutral-900 dark:text-white">
              {{ labels.phone }}
            </h3>
            <ul class="mt-2 space-y-1.5">
              <li
                v-for="(phone, index) in phoneItems"
                :key="index"
              >
                <a
                  :href="`tel:${phone.href}`"
                  class="text-sm leading-6 text-neutral-800 transition hover:text-primary dark:text-neutral-200"
                >
                  {{ phone.label }}
                </a>
              </li>
            </ul>
          </div>
        </section>

        <section
          v-if="showAddress && addressItems.length"
          class="flex min-w-0 gap-3"
          :class="isContactOnly ? 'rounded-2xl border border-neutral-200/80 bg-white/70 p-4 dark:border-neutral-800 dark:bg-neutral-900/50' : ''"
        >
          <span class="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary" aria-hidden="true">
            <UIcon name="i-lucide-map-pin" class="size-4" />
          </span>
          <div class="min-w-0">
            <h3 class="text-xs font-semibold tracking-wide text-neutral-900 dark:text-white">
              {{ labels.address }}
            </h3>
            <ul class="mt-2 space-y-2">
              <li
                v-for="(line, index) in addressItems"
                :key="index"
                class="text-sm leading-7 text-neutral-700 dark:text-neutral-300"
              >
                {{ line }}
              </li>
            </ul>
          </div>
        </section>

        <section
          v-if="showHours && hourItems.length"
          class="flex min-w-0 gap-3 rounded-2xl border border-neutral-200/80 bg-white/70 p-4 sm:col-span-2 sm:p-5 lg:col-span-1 dark:border-neutral-800 dark:bg-neutral-900/50"
        >
          <span class="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary" aria-hidden="true">
            <UIcon name="i-lucide-clock" class="size-4" />
          </span>
          <div class="min-w-0 flex-1">
            <h3 class="text-sm font-semibold text-neutral-900 dark:text-white">
              {{ labels.hours }}
            </h3>
            <ul class="mt-3 divide-y divide-neutral-200/80 dark:divide-neutral-800">
              <li
                v-for="(row, index) in hourItems"
                :key="index"
                class="flex min-w-0 items-center justify-between gap-3 py-2.5 text-sm first:pt-0 last:pb-0"
              >
                <div class="min-w-0 flex-1">
                  <span class="font-medium text-neutral-800 dark:text-neutral-200">
                    {{ row.day || row.time }}
                  </span>
                </div>
                <span
                  v-if="row.time"
                  class="max-w-[55%] shrink-0 rounded-lg bg-primary/10 px-2.5 py-1 text-center text-xs font-semibold leading-5 tabular-nums text-primary"
                  dir="ltr"
                >{{ row.time }}</span>
              </li>
            </ul>
          </div>
        </section>
      </div>

      <div
        class="flex flex-wrap items-center justify-between gap-3 text-xs text-neutral-500 dark:text-neutral-500"
        :class="hasContact || !isSimple || (showEnamad && enamadSeal) ? 'mt-8 border-t border-neutral-200 pt-5 dark:border-neutral-800' : ''"
      >
        <p v-if="showCopyright && !isSimple">
          {{ resolvedCopyright }}
        </p>
        <a
          v-if="showEnamad && enamadSeal"
          :href="enamadSeal.href"
          target="_blank"
          rel="noopener"
          referrerpolicy="origin"
          aria-label="نشان اعتماد الکترونیکی"
        >
          <img
            :src="enamadSeal.src"
            alt=""
            class="h-auto max-h-20 w-auto cursor-pointer"
            referrerpolicy="origin"
          >
        </a>
        <a
          v-if="isSimple && showCreatorLabel && resolvedCreatorLabel"
          class="transition hover:text-primary"
          :href="creatorUrl || 'https://aradcore.com'"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ resolvedCreatorLabel }}
        </a>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { parseEnamadEmbedCode } from '~/utils/enamad'

const dir = useDir()
const pageLocale = useState<string>('page-locale', () => 'fa')

type LocalizedString = string | Record<string, string>

const props = withDefaults(
  defineProps<{
    variant?: 'simple' | 'columns' | 'contact'
    showBrand?: boolean
    showColumns?: boolean
    showSocialLinks?: boolean
    showPhone?: boolean
    showAddress?: boolean
    showHours?: boolean
    showCopyright?: boolean
    showCreatorLabel?: boolean
    showEnamad?: boolean
    enamadCode?: string
    phone?: LocalizedString
    address?: LocalizedString
    hours?: LocalizedString
    creatorLabel?: LocalizedString
    creatorUrl?: string
    logoText?: LocalizedString
    logoImage?: string
    tagline?: LocalizedString
    columns?: Array<{
      title?: LocalizedString
      links?: Array<{ label?: LocalizedString; to?: string }>
    }>
    socialLinks?: Array<{ icon: string; url: string }>
    copyright?: LocalizedString
    builderPreview?: boolean
    onNavigate?: (to: string) => void
  }>(),
  {
    variant: 'columns',
    showBrand: true,
    showColumns: true,
    showSocialLinks: true,
    showPhone: true,
    showAddress: true,
    showHours: true,
    showCopyright: true,
    showCreatorLabel: true,
    showEnamad: true,
    columns: () => [],
    socialLinks: () => [],
    builderPreview: false,
  },
)

function resolveVal(value: unknown, loc: string): any {
  if (value == null) return ''
  if (Array.isArray(value)) return value.map((item) => resolveVal(item, loc))
  if (typeof value === 'object') {
    const rec = value as Record<string, unknown>
    if (rec[loc] !== undefined) return rec[loc]
    return Object.fromEntries(
      Object.entries(rec).map(([k, v]) => [k, resolveVal(v, loc)]),
    )
  }
  return value
}

const isSimple = computed(() => props.variant === 'simple' || props.variant === 'contact')
const isContactOnly = computed(() => props.variant === 'contact')
const enamadSeal = computed(() => parseEnamadEmbedCode(props.enamadCode ?? ''))

const resolvedPhone = computed(() => {
  const t = props.phone
  if (typeof t === 'string') return t
  return t ? String(resolveVal(t, pageLocale.value)) : ''
})

const resolvedAddress = computed(() => {
  const t = props.address
  if (typeof t === 'string') return t
  return t ? String(resolveVal(t, pageLocale.value)) : ''
})

const resolvedHours = computed(() => {
  const t = props.hours
  if (typeof t === 'string') return t
  return t ? String(resolveVal(t, pageLocale.value)) : ''
})

const resolvedCreatorLabel = computed(() => {
  const t = props.creatorLabel
  if (typeof t === 'string') return t
  return t ? String(resolveVal(t, pageLocale.value)) : ''
})

const labels = computed(() =>
  pageLocale.value === 'fa'
    ? { phone: 'تماس', address: 'نشانی', hours: 'ساعات پذیرش' }
    : { phone: 'Contact', address: 'Address', hours: 'Hours' },
)

const phoneItems = computed(() =>
  resolvedPhone.value
    .split('|')
    .map(part => part.trim())
    .filter(Boolean)
    .map(label => ({
      label,
      href: label.replace(/[^\d+]/g, '') || label,
    })),
)

const addressItems = computed(() => {
  const raw = resolvedAddress.value.trim()
  if (!raw) return []
  const lines = raw.split(/\n+/).map(line => line.trim()).filter(Boolean)
  if (lines.length > 1) return lines
  const places = raw.split(/\s+(?=مرکز\s)/).map(line => line.trim()).filter(Boolean)
  return places.length ? places : [raw]
})

function splitHourDetail(detail: string): { time: string } {
  const times = [...detail.matchAll(/\d{1,2}:\d{2}\s*[–-]\s*\d{1,2}:\d{2}/g)]
    .map(match => match[0].replace(/\s/g, ''))
  return { time: times.join('، ') }
}

const hourItems = computed(() =>
  resolvedHours.value
    .split(/\n+/)
    .map(line => line.trim())
    .filter(Boolean)
    .map((line) => {
      const idx = line.indexOf(':')
      const day = idx > 0 && idx <= 24 ? line.slice(0, idx).trim() : ''
      const detail = day ? line.slice(idx + 1).trim() : line
      const { time } = splitHourDetail(detail)
      return { day, time }
    })
    .filter(row => row.day || row.time),
)

const visibleColumns = computed(() => props.showColumns ? resolvedColumns.value : [])

const hasContact = computed(() =>
  (props.showPhone && phoneItems.value.length > 0)
  || (props.showAddress && addressItems.value.length > 0)
  || (props.showHours && hourItems.value.length > 0),
)

const resolvedLogoText = computed(() => {
  const t = props.logoText
  if (typeof t === 'string') return t
  return t ? String(resolveVal(t, pageLocale.value)) : ''
})

const resolvedTagline = computed(() => {
  const t = props.tagline
  if (typeof t === 'string') return t
  return t ? String(resolveVal(t, pageLocale.value)) : ''
})

const resolvedCopyright = computed(() => {
  const t = props.copyright
  if (typeof t === 'string') return t
  return t ? String(resolveVal(t, pageLocale.value)) : '© Your Site'
})

const resolvedColumns = computed(() =>
  (props.columns ?? []).map((col) => ({
    title:
      typeof col.title === 'string'
        ? col.title
        : (resolveVal(col.title, pageLocale.value) as string) || '',
    links: (col.links ?? []).map((link) => ({
      label:
        typeof link.label === 'string'
          ? link.label
          : (resolveVal(link.label, pageLocale.value) as string) || '',
      to: link.to ?? '#',
    })),
  })),
)

const resolvedSocialLinks = computed(() => (props.socialLinks ?? []).slice(0, 6))

function handleNavClick(to: string | undefined) {
  if (!to) return
  if (props.builderPreview && props.onNavigate) props.onNavigate(to)
}
</script>
